import React from 'react';
import { AlertTriangle } from 'lucide-react';
import ColumnsPicker, { useVisibleColumns, type ColumnDef } from './ColumnsPicker';
import { formatDurationMinutes } from './chartUtils';

export interface StudyRow {
  accession: string;
  patient: string;
  modality: string;
  department: string;
  radiologist: string;
  priority: string;
  status: string;
  modifiedUser?: string;
  tatMinutes: number | null;
  tatExceeded: boolean;
  overdue?: boolean;
  tatNotConfigured?: boolean;
}

interface AnalyticsStudiesTableProps {
  rows: StudyRow[];
  page: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

const STUDY_COLUMNS: ColumnDef<StudyRow>[] = [
  { key: 'accession', label: 'Accession' },
  { key: 'patient', label: 'Patient' },
  { key: 'modality', label: 'Modality' },
  { key: 'department', label: 'Department' },
  { key: 'radiologist', label: 'Radiologist' },
  { key: 'priority', label: 'Priority' },
  { key: 'status', label: 'Status' },
  { key: 'modifiedUser', label: 'Modified user' },
  { key: 'tat', label: 'TAT' },
];

function cellValue(value: unknown): string {
  if (value == null) {
    return '—';
  }
  const text = String(value).trim();
  return text || '—';
}

function priorityClass(priority: string): string {
  const p = priority.toLowerCase();
  if (p.includes('urgent')) {
    return 'analytics-priority-badge analytics-priority-badge--urgent';
  }
  if (p.includes('vip')) {
    return 'analytics-priority-badge analytics-priority-badge--vip';
  }
  if (p.includes('mlc')) {
    return 'analytics-priority-badge analytics-priority-badge--mlc';
  }
  return 'analytics-priority-badge analytics-priority-badge--routine';
}

function renderTatCell(row: StudyRow) {
  if (row.tatNotConfigured) {
    return <span className="analytics-tat-not-configured">TAT Not Configured</span>;
  }
  if (row.tatMinutes == null) {
    return '—';
  }
  const text = formatDurationMinutes(row.tatMinutes);
  if (row.tatExceeded) {
    return <span className="analytics-tat-exceeded">{text}</span>;
  }
  return text;
}

export default function AnalyticsStudiesTable({
  rows,
  page,
  pageSize,
  totalPages,
  totalItems,
  onPageChange,
  onPageSizeChange,
}: Readonly<AnalyticsStudiesTableProps>) {
  const { visibleColumns, visibleKeys, toggleColumn } = useVisibleColumns(
    STUDY_COLUMNS,
    'analytics-studies-columns-v2'
  );

  const renderCell = (key: string, row: StudyRow) => {
    switch (key) {
      case 'accession':
        return (
          <span className="analytics-table-cell-content">
            {row.overdue ? (
              <AlertTriangle
                size={14}
                className="analytics-overdue-icon"
                aria-label="Overdue"
              />
            ) : null}
            {cellValue(row.accession)}
          </span>
        );
      case 'priority':
        return <span className={priorityClass(row.priority)}>{cellValue(row.priority)}</span>;
      case 'tat':
        return renderTatCell(row);
      default:
        return cellValue((row as unknown as Record<string, unknown>)[key]);
    }
  };

  return (
    <section className="analytics-section-card">
      <div className="analytics-section-header">
        <h3 className="analytics-section-title">Studies</h3>
        <ColumnsPicker
          columns={STUDY_COLUMNS}
          storageKey="analytics-studies-columns-v2"
          visibleKeys={visibleKeys}
          toggleColumn={toggleColumn}
        />
      </div>
      {rows.length === 0 ? (
        <p className="analytics-empty">No records found.</p>
      ) : (
        <>
          <div className="analytics-table-wrap">
            <table className="analytics-table">
              <thead>
                <tr>
                  {visibleColumns.map(col => (
                    <th key={col.key}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={`${row.accession}-${row.patient}-${index}`}>
                    {visibleColumns.map(col => (
                      <td key={col.key}>{renderCell(col.key, row)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="analytics-table-footer">
            <label className="analytics-table-page-size">
              Rows per page
              <select
                className="analytics-select"
                value={pageSize}
                onChange={e => onPageSizeChange(Number(e.target.value))}
              >
                {[10, 25, 50].map(n => (
                  <option
                    key={n}
                    value={n}
                  >
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <span>
              Page {page} of {totalPages || 1} ({totalItems} studies)
            </span>
            <div className="analytics-table-pagination">
              <button
                type="button"
                className="analytics-secondary-btn"
                disabled={page <= 1}
                onClick={() => onPageChange(page - 1)}
              >
                Previous
              </button>
              <button
                type="button"
                className="analytics-secondary-btn"
                disabled={page >= totalPages}
                onClick={() => onPageChange(page + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
